import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaPaw, FaHeart, FaImage, FaExclamationTriangle, FaAngleRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import i18n from '../../i18n'; // Import i18n instance
import '../../style/PetPage.css'; 
import petSpecies from '../../data/petSpecies';
import dogBreeds from '../../data/dogBreeds';
import catBreeds from '../../data/catBreeds';
import rabbitBreeds from '../../data/rabbitBreeds';
import colors from '../../data/colors';
import eyeColors from '../../data/eyeColors';
import behaviorWithPeople from '../../data/behaviorWithPeople';
import behaviorWithDogs from '../../data/behaviorWithDogs';
import behaviorWithCats from '../../data/behaviorWithCats';
import sexOptions from '../../data/sexOptions'; 
import sizeOptions from '../../data/sizeOptions';
import furAmountOptions from '../../data/furAmountOptions'; 
import furLengthOptions from '../../data/furLengthOptions';
import dewormingMethods from '../../data/dewormingMethods'; 
import dewormingTypes from '../../data/dewormingTypes';
import petVaccines from '../../data/petVaccines'; 

import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
// ...existing imports...

const PetPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(); 
    const [pet, setPet] = useState(null);
    const [weightData, setWeightData] = useState([]);
    const [latestWeight, setLatestWeight] = useState(null);
    const [vaccines, setVaccines] = useState([]);
    const [dewormings, setDewormings] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('A'); 
    // Lightbox state
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [lightboxImages, setLightboxImages] = useState([]); // Array of images to show in Lightbox
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchPet = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Pet not found');
                }

                const data = await response.json();
                setPet(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };
        const fetchWeightHistory = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;
        
                if (!token) {
                    throw new Error('No authentication token found');
                }
        
                const response = await fetch(`${baseUrl}/api/pets/${id}/weight-history`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
        
                if (!response.ok) {
                    throw new Error('Failed to fetch weight history');
                }
        
                const data = await response.json();
                const sortedWeightHistory = data.weightHistory.sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
                setWeightData(sortedWeightHistory);
                setLatestWeight(sortedWeightHistory[0] || null); // Get the most recent weight entry
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        const fetchVaccines = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch vaccines');
                }

                const data = await response.json();
                console.log('Fetched vaccines:', data); 
                setVaccines(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        const fetchDewormings = async () => { // Fetch dewormings
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch dewormings');
                }

                const data = await response.json();
                setDewormings(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchPet();
        fetchWeightHistory();
        fetchVaccines();
        fetchDewormings(); 
    }, [id, baseUrl]);


    const handleToggleStatus = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const updatedStatus = pet.status === 'normal' ? 'lost' : 'normal';

            const response = await fetch(`${baseUrl}/api/pets/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: updatedStatus }),
            });

            if (!response.ok) {
                throw new Error('Failed to update pet status');
            }

            const updatedPet = await response.json();
            setPet(updatedPet);
            setLoading(false);
        } catch (error) {
            setError(error.message);
            console.error(error);
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        navigate(`/edit-pet/${id}`);
    };


    const getTranslatedLabel = (options, value) => {
        console.log('Options:', options);
        console.log('Value:', value);
        const option = options.find((o) => o.value === value);
        console.log('Found Option:', option);
        return option ? option.label[i18n.language] : t('general.unknown'); // Use translation for "unknown"
    };
    
    const getPetTypeLabel = (petTypeValue) => {
        const normalizedValue = petTypeValue?.trim().toLowerCase(); // Normalize the value
        return getTranslatedLabel(petSpecies, normalizedValue);
    };

    const getBreedLabel = (breedValue) => {
        const allBreeds = [...dogBreeds, ...catBreeds, ...rabbitBreeds]; // Combine all breeds
    
        // If breedValue is an array, map over it and join the translated labels
        if (Array.isArray(breedValue)) {
            return breedValue
                .map((value) => getTranslatedLabel(allBreeds, value)) // Translate each breed
                .join(', '); // Join the translated labels with a comma
        }
    
        // If breedValue is a single value, translate it directly
        return getTranslatedLabel(allBreeds, breedValue);
    };

    const getColorLabel = (colorValue) => {
        return getTranslatedLabel(colors, colorValue);
    };

    const getEyeColorLabel = (eyeColorValue) => {
        return getTranslatedLabel(eyeColors, eyeColorValue);
    };

    const getBehaviorLabel = (behaviorValue, behaviorData) => {
        return getTranslatedLabel(behaviorData, behaviorValue);
    };

    const getSexLabel = (sexValue) => {
        return getTranslatedLabel(sexOptions, sexValue);
    };

    const getSizeLabel = (sizeValue) => {
        return getTranslatedLabel(sizeOptions, sizeValue);
    };

    const getFurAmountLabel = (amountValue) => {
        return getTranslatedLabel(furAmountOptions, amountValue);
    };

    const getFurLengthLabel = (lengthValue) => {
        return getTranslatedLabel(furLengthOptions, lengthValue);
    };

    const getDewormingMethodLabel = (methodValue) => {
        return getTranslatedLabel(dewormingMethods, methodValue);
    };
    
    const getDewormingTypeLabel = (typeValue) => {
        return getTranslatedLabel(dewormingTypes, typeValue);
    };

    const formatName = (name) => {
        return name.replace(/_/g, ' '); // Replace underscores with spaces
    };

    const calculateAge = (birthdate) => {
        if (!birthdate) return t('general.unknown');
        const birthDate = new Date(birthdate);
        const today = new Date();

        const years = today.getFullYear() - birthDate.getFullYear();
        const months = today.getMonth() - birthDate.getMonth();
        const days = today.getDate() - birthDate.getDate();

        let ageString = '';

        if (years > 0) {
            ageString += `${years} ${t('general.year', { count: years })} `;
        }
        if (months > 0) {
            ageString += `${months} ${t('general.month', { count: months })} `;
        }
        if (years === 0 && months === 0 && days > 0) {
            ageString += `${days} ${t('general.day', { count: days })}`;
        }

        return ageString.trim() || t('general.unknownAge');
    };


    if (error) {
        return <div className="error-message">Error: {error}</div>;
    }

    if (!pet) {
        return <div className="loading-message">{t('general.loading')}</div>;
    }

    // Extract the profile image or fallback to the first image
    const profileImage = pet.images?.find((image) => image.tag === 'profile')?.url ;

    // Helper to get image arrays
    const generalImages = pet.images?.filter((image) => image.tag === 'general') || [];
    const lostImages = pet.images?.filter((image) => image.tag === 'lost') || [];

    return (
        <div className="pet-page p-6 bg-dafauna-background">
            <div className="pet-container max-w-4xl mx-auto">
                <div className="pet-details-container">
                <div className="pet-header bg-dafauna-background rounded-xl shadow-md p-6">
                    <div className="pet-header-left">
                        {profileImage ? (
                            <img
                                src={profileImage} // Use the profile image or fallback to the first image
                                alt={`${pet.name} profile`}
                                className="pet-profile-picture"
                                onClick={() => navigate(`/pet/${id}/update-profile-image`)}
                            />
                        ) : (
                            <div className="placeholder-picture text-dafauna-gray" onClick={() => navigate(`/pet/${id}/update-profile-image`)}>{t('generalPet.imageAvailable')}</div> // Placeholder if no image exists
                        )}
                    </div>
                    <div className="pet-header-right">
                        <div className="pet-name-container flex items-center gap-2">
                            <h1 className="pet-name text-dafauna-darkText text-xl font-bold">{pet.name}</h1>
                            <FaEdit
                                className="edit-icon text-dafauna-primary cursor-pointer hover:text-dafauna-primaryLight"
                                onClick={() => navigate(`/edit-pet/${id}`)} // Redirect to EditPetPage
                                title="Edit Pet"
                            />
                        </div>
                        <div className="pet-type-container flex items-center gap-2 text-dafauna-gray">
                            <p className="pet-type">{getPetTypeLabel(pet.petType)}</p>
                            <p className="pet-age">|</p>
                            <p className="pet-age">
                                {pet.general?.birthDate
                                    ? calculateAge(pet.general?.birthDate)
                                    : 'N/A'}
                            </p>
                        </div>
                        {/* Conditional Button Based on Pet Status */}
                        {pet.status === 'lost' ? (
                            <div
                                onClick={() => navigate(`/lost-pet/${id}`)}
                                className="lost-details-btn flex items-center gap-2 text-red-500 hover:text-red-700 cursor-pointer"
                                title={t('generalPet.viewLostPetDetails')}
                            >
                                <FaExclamationTriangle className="siren-icon text-red-500" /> {/* Siren icon */}
                                {t('generalPet.viewLostPetDetails')}
                            </div>
                        ) : (
                            <div
                                onClick={() => navigate(`/pet/${id}/report-lost`)}
                                className="report-lost-btn flex items-center gap-2 text-dafauna-primary hover:text-dafauna-primaryLight cursor-pointer"
                                title={t('generalPet.reportAsLost')}
                            >
                                <FaExclamationTriangle className="siren-icon text-dafauna-primary" /> {/* Siren icon */}
                                {t('generalPet.reportAsLost')}
                            </div>
                        )}
                    </div>
                </div>

            {/* Navigation Buttons */}
            <div className="section-navigation">
                <button
                    className={`nav-btn ${activeSection === 'A' ? 'active' : ''}`}
                    onClick={() => setActiveSection('A')}
                >
                    <FaPaw /> {/* Pet icon */}
                </button>
                <button
                    className={`nav-btn ${activeSection === 'B' ? 'active' : ''}`}
                    onClick={() => setActiveSection('B')}
                >
                    <FaHeart /> {/* Heart icon */}
                </button>
                <button
                    className={`nav-btn ${activeSection === 'C' ? 'active' : ''}`}
                    onClick={() => setActiveSection('C')}
                >
                    <FaImage /> {/* Image icon */}
                </button>
            </div>

            {/* Conditional Rendering Based on Active Section */}
            <div className="section-content">
                {activeSection === 'A' && (
                    <div>
                        {/* Description Section */}
                        <div className="description-section border-t border-gray-300 mt-4 pt-4">
                            <h3 className="text-dafauna-darkText text-lg font-bold mb-2">{t('generalPet.description')}</h3>
                            <p className="text-dafauna-gray text-sm">{pet.description || t('generalPet.noDescription')}</p>
                        </div>

                        {/* General Section */}
                        <div className="general-section border-t border-gray-300 mt-6 pt-4">
                            <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.general')}</h3>
                            <div className="general-info flex flex-col gap-4">
                                <div className="general-left">
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.gender')}</p>
                                        <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getSexLabel(pet.general?.sex || 'unknown')}</div>
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.size')}</p>
                                        <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getSizeLabel(pet.general?.size || 'unknown')}</div>
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.breed')}</p>
                                        {pet.general?.breeds?.length > 0 ? (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getBreedLabel(pet.general.breeds)}</div>
                                        ) : (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                        )}
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.fur')}</p>
                                        {pet.general?.fur?.hasFur ? (
                                            <>
                                                <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getFurAmountLabel(pet.general.fur.amount || 'unknown')}</div>
                                                <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getFurLengthLabel(pet.general.fur.length || 'unknown')}</div>
                                            </>
                                        ) : (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('generalPet.noFur')}</div>
                                        )}
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.primaryColor')}</p>
                                        {pet.general?.primaryColor ? (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getColorLabel(pet.general.primaryColor)}</div>
                                        ) : (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                        )}
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.secondaryColors')}</p>
                                        {pet.general?.secondaryColors?.length > 0 ? (
                                            pet.general.secondaryColors.map((color, index) => (
                                                <div key={index} className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getColorLabel(color)}</div>
                                            ))
                                        ) : (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                        )}
                                    </div>
                                    <div className="horizontal-section flex items-center gap-4">
                                        <p className="text-dafauna-darkText font-medium">{t('generalPet.eyeColors')}</p>
                                        {pet.general?.eyeColors?.length > 0 ? (
                                            pet.general.eyeColors.map((eyeColor, index) => (
                                                <div key={index} className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getEyeColorLabel(eyeColor)}</div>
                                            ))
                                        ) : (
                                            <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Behavior Section */}
                        <div className="behavior-section border-t border-gray-300 mt-6 pt-4">
                            <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.behaviorWith')}</h3>
                            <div className="horizontal-section flex items-center gap-4">
                                <p className="text-dafauna-darkText font-medium">{t('generalPet.withPeople')}</p>
                                {pet.behavior?.withPeople?.length > 0 ? (
                                    pet.behavior.withPeople.map((behavior, index) => (
                                        <div key={index} className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getBehaviorLabel(behavior, behaviorWithPeople)}</div>
                                    ))
                                ) : (
                                    <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                )}
                            </div>
                            <div className="horizontal-section flex items-center gap-4">
                                <p className="text-dafauna-darkText font-medium">{t('generalPet.withDogs')}</p>
                                {pet.behavior?.withDogs?.length > 0 ? (
                                    pet.behavior.withDogs.map((behavior, index) => (
                                        <div key={index} className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getBehaviorLabel(behavior, behaviorWithDogs)}</div>
                                    ))
                                ) : (
                                    <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                )}
                            </div>
                            <div className="horizontal-section flex items-center gap-4">
                                <p className="text-dafauna-darkText font-medium">{t('generalPet.withCats')}</p>
                                {pet.behavior?.withCats?.length > 0 ? (
                                    pet.behavior.withCats.map((behavior, index) => (
                                        <div key={index} className="tag bg-dafauna-primaryLight text-dafauna-darkText">{getBehaviorLabel(behavior, behaviorWithCats)}</div>
                                    ))
                                ) : (
                                    <div className="tag bg-dafauna-primaryLight text-dafauna-darkText">{t('general.unknown')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeSection === 'B' && (
                    <div>
                        <div className="health-section border-t border-gray-300 mt-6 pt-4">
                            <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.general')}</h3>
                            <div className="health-info flex justify-between gap-5">
                                {/* Left Side */}
                                <div className="health-left flex flex-col gap-2">
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('generalPet.chip')}:</strong> {pet.health?.hasChip ? pet.health?.chipNumber || t('generalPet.noChipNumber') : t('generalPet.noChip')}
                                    </p>
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('generalPet.tattoo')}:</strong> {pet.health?.hasTattoo ? t('general.yes') : t('general.no')}
                                    </p>
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('generalPet.castrated')}:</strong> {pet.health?.castrated ? t('general.yes') : t('general.no')}
                                    </p>
                                    <div className="weight-subsection flex items-center gap-2">
                                        <p className="text-dafauna-darkText text-sm">
                                            <strong>{t('generalPet.weight')}:</strong> {latestWeight ? `${latestWeight.weightKg} kg` : t('general.unknown')}
                                        </p>
                                        <button
                                            onClick={() => navigate(`/pet/${id}/weight-history`, { state: { weightData } })}
                                            className="btn view-history-btn bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                {/* Right Side */}
                                <div className="health-right flex flex-col gap-2">
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('condition.allergies')}:</strong> {pet.health?.conditions?.filter((condition) => condition.type === 'allergy').length > 0 ? t('general.yes') : t('general.no')}
                                    </p>
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('condition.diseases')}:</strong> {pet.health?.conditions?.filter((condition) => condition.type === 'disease').length > 0 ? t('general.yes') : t('general.no')}
                                    </p>
                                    <p className="text-dafauna-darkText text-sm">
                                        <strong>{t('condition.disabilities')}:</strong> {pet.health?.conditions?.filter((condition) => condition.type === 'disability').length > 0 ? t('general.yes') : t('general.no')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Vaccine Section */}
                        <div className="vaccine-section border-t border-gray-300 mt-6 pt-4">
                            <div className="section-header flex justify-between items-center">
                                <h3 className="text-dafauna-darkText text-lg font-bold">{t('generalPet.vaccines')}</h3>
                                <p
                                    onClick={() => navigate(`/pet/${id}/add-vaccine`)}
                                    className="clickable-text bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight cursor-pointer"
                                >
                                    +
                                </p>
                            </div>
                            {vaccines.length > 0 ? (
                                <table className="table vaccine-table w-full border border-gray-300 rounded-md">
                                    <thead>
                                        <tr className="bg-dafauna-primaryLight text-dafauna-darkText">
                                            <th className="px-4 py-2">{t('general.name')}</th>
                                            <th className="px-4 py-2">{t('general.next')}</th>
                                            <th className="px-4 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {vaccines.map((vaccine) => (
                                        <tr key={vaccine._id} className="hover:bg-gray-100">
                                            <td className="px-4 py-2">
                                                {vaccine.name
                                                    .map((name) =>
                                                        name
                                                            .split(' ')
                                                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                            .join(' ')
                                                    )
                                                    .join(' / ')}
                                            </td>
                                            <td className="px-4 py-2">
                                                {vaccine.nextGivenDate
                                                    ? new Date(vaccine.nextGivenDate).toLocaleDateString()
                                                    : t('general.unknown')}
                                            </td>
                                            <td className="px-4 py-2">
                                                <button
                                                    onClick={() => navigate(`/pet/${id}/vaccine/${vaccine._id}`)}
                                                    className="view-details-btn bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight"
                                                >
                                                    <FaAngleRight />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-dafauna-gray text-sm">{t('generalPet.noVaccines')}</p>
                            )}
                        </div>

                        {/* Deworming Section */}
                        <div className="deworming-section border-t border-gray-300 mt-6 pt-4">
                            <div className="section-header flex justify-between items-center">
                                <h3 className="text-dafauna-darkText text-lg font-bold">{t('generalPet.dewormings')}</h3>
                                <p
                                    onClick={() => navigate(`/pet/${id}/add-deworming`)}
                                    className="clickable-text bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight cursor-pointer"
                                >
                                    +
                                </p>
                            </div>
                            {dewormings.length > 0 ? (
                                <table className="table deworming-table w-full border border-gray-300 rounded-md">
                                    <thead>
                                        <tr className="bg-dafauna-primaryLight text-dafauna-darkText">
                                            <th className="px-4 py-2">{t('general.method')}</th>
                                            <th className="px-4 py-2">{t('general.type')}</th>
                                            <th className="px-4 py-2">{t('general.next')}</th>
                                            <th className="px-4 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dewormings.map((deworming) => (
                                            <tr key={deworming._id} className="hover:bg-gray-100">
                                                <td className="px-4 py-2">{getDewormingMethodLabel(deworming.method)}</td>
                                                <td className="px-4 py-2">{getDewormingTypeLabel(deworming.type)}</td>
                                                <td className="px-4 py-2">{deworming.nextGivenDate ? new Date(deworming.nextGivenDate).toLocaleDateString() : t('general.unknown')}</td>
                                                <td className="px-4 py-2">
                                                    <button
                                                        onClick={() => navigate(`/pet/${id}/deworming/${deworming._id}`)}
                                                        className="view-details-btn bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight"
                                                    >
                                                        <FaAngleRight />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-dafauna-gray text-sm">{t('generalPet.noDewormings')}</p>
                            )}
                        </div>
                        <div className="allergies-section mt-6 pt-4 border-t border-gray-300">
                            <div className="section-header flex justify-between items-center">
                                <h3 className="text-dafauna-darkText text-lg font-bold">{t('condition.allergies')}</h3>
                                <p
                                    onClick={() => navigate(`/pet/${id}/add-allergy`)}
                                    className="clickable-text bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight cursor-pointer"
                                >
                                    +
                                </p>
                            </div>
                            {pet.health?.conditions?.filter((condition) => condition.type === 'allergy').length > 0 ? (
                                <div className="allergy-buttons flex flex-wrap gap-2">
                                    {pet.health.conditions
                                        .filter((condition) => condition.type === 'allergy')
                                        .map((allergy) => (
                                            <button
                                                key={allergy._id}
                                                onClick={() => navigate(`/pet/${id}/conditions/${allergy._id}`)}
                                                className="tag allergy-btn bg-dafauna-primaryLight text-dafauna-darkText hover:bg-dafauna-primary hover:text-white"
                                            >
                                                {allergy.name}
                                            </button>
                                        ))}
                                </div>
                            ) : (
                                <p className="text-dafauna-gray text-sm">{t('condition.noAllergies')}</p>
                            )}
                        </div>
                        <div className="diseases-section mt-6 pt-4 border-t border-gray-300">
                            <div className="section-header flex justify-between items-center">
                                <h3 className="text-dafauna-darkText text-lg font-bold">{t('condition.diseases')}</h3>
                                <p
                                    onClick={() => navigate(`/pet/${id}/add-disease`)}
                                    className="clickable-text bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight cursor-pointer"
                                >
                                    +
                                </p>
                            </div>
                            {pet.health?.conditions?.filter((condition) => condition.type === 'disease').length > 0 ? (
                                <div className="disease-buttons flex flex-wrap gap-2">
                                    {pet.health.conditions
                                        .filter((condition) => condition.type === 'disease')
                                        .map((disease) => (
                                            <button
                                                key={disease._id}
                                                onClick={() => navigate(`/pet/${id}/conditions/${disease._id}`)}
                                                className="tag disease-btn bg-dafauna-primaryLight text-dafauna-darkText hover:bg-dafauna-primary hover:text-white"
                                            >
                                                {disease.name}
                                            </button>
                                        ))}
                                </div>
                            ) : (
                                <p className="text-dafauna-gray text-sm">{t('condition.noDiseases')}</p>
                            )}
                        </div>
                        <div className="disabilities-section mt-6 pt-4 border-t border-gray-300">
                            <div className="section-header flex justify-between items-center">
                                <h3 className="text-dafauna-darkText text-lg font-bold">{t('condition.disabilities')}</h3>
                                <p
                                    onClick={() => navigate(`/pet/${id}/add-disability`)}
                                    className="clickable-text bg-dafauna-primary text-white rounded-md px-2 py-1 hover:bg-dafauna-primaryLight cursor-pointer"
                                >
                                    +
                                </p>
                            </div>
                            {pet.health?.conditions?.filter((condition) => condition.type === 'disability').length > 0 ? (
                                <div className="disability-buttons flex flex-wrap gap-2">
                                    {pet.health.conditions
                                        .filter((condition) => condition.type === 'disability')
                                        .map((disability) => (
                                            <button
                                                key={disability._id}
                                                onClick={() => navigate(`/pet/${id}/conditions/${disability._id}`)}
                                                className="tag disability-btn bg-dafauna-primaryLight text-dafauna-darkText hover:bg-dafauna-primary hover:text-white"
                                            >
                                                {disability.name}
                                            </button>
                                        ))}
                                </div>
                            ) : (
                                <p className="text-dafauna-gray text-sm">{t('condition.noDisabilities')}</p>
                            )}
                        </div>
                    </div>
                )}
                {activeSection === 'C' && (
                    <div>
                        {/* General Images */}
                        <div className="pet-images-container flex justify-between items-center align-items-center">
                            <h3 className="text-dafauna-darkText text-lg font-bold">{t('generalPet.picturesOfYourBuddy')}</h3>
                            <button
                                onClick={() => navigate(`/pet/${id}/manage-images`)}
                                className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight"
                            >
                                {t('general.edit')}
                            </button>
                        </div>
                        {generalImages.length > 0 ? (
                            <div className="pet-images flex flex-wrap justify-center gap-4">
                                {generalImages.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image.url}
                                        alt={t('generalPet.generalImageAlt', { index: index + 1 })}
                                        className="pet-image w-36 h-36 object-cover rounded-lg border border-gray-300 hover:scale-105 transition-transform cursor-pointer"
                                        onClick={() => {
                                            setLightboxImages(generalImages.map(img => img.url));
                                            setCurrentImageIndex(index);
                                            setIsLightboxOpen(true);
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-dafauna-gray text-sm text-center">{t('generalPet.noPicturesAvailable')}</p>
                        )}

                        {/* Lost Images */}
                        {lostImages.length > 0 && (
                            <>
                                <div className="pet-images-container flex justify-between items-center">
                                    <h3 className="text-dafauna-darkText text-lg font-bold">{t('generalPet.lostPictures')}</h3>
                                    <button
                                        onClick={() => navigate(`/pet/${id}/manage-lost-images`)}
                                        className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight"
                                    >
                                        {t('general.edit')}
                                    </button>
                                </div>
                                <div className="pet-images flex flex-wrap justify-center gap-4">
                                    {lostImages.map((image, index) => (
                                        <img
                                            key={index}
                                            src={image.url}
                                            alt={t('generalPet.lostImageAlt', { index: index + 1 })}
                                            className="pet-image w-36 h-36 object-cover rounded-lg border border-gray-300 hover:scale-105 transition-transform cursor-pointer"
                                            onClick={() => {
                                                setLightboxImages(lostImages.map(img => img.url));
                                                setCurrentImageIndex(index);
                                                setIsLightboxOpen(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Lightbox for images */}
                        {isLightboxOpen && lightboxImages.length > 0 && (
                            <Lightbox
                                mainSrc={lightboxImages[currentImageIndex]}
                                nextSrc={lightboxImages[(currentImageIndex + 1) % lightboxImages.length]}
                                prevSrc={lightboxImages[(currentImageIndex + lightboxImages.length - 1) % lightboxImages.length]}
                                onCloseRequest={() => setIsLightboxOpen(false)}
                                onMovePrevRequest={() =>
                                    setCurrentImageIndex((currentImageIndex + lightboxImages.length - 1) % lightboxImages.length)
                                }
                                onMoveNextRequest={() =>
                                    setCurrentImageIndex((currentImageIndex + 1) % lightboxImages.length)
                                }
                            />
                        )}
                    </div>
                )}
            </div>
            </div>
            </div>
        </div>
    );
};

export default PetPage;